'use client';

import type { RefObject } from 'react';
import { startTransition, useEffect, useRef, useState } from 'react';
import type { Rect } from '../layouts/types';
import { useSize } from './useSize';

type Options = {
  defaultHeight?: number;
  defaultWidth?: number;
  initialY?: number;
  initialX?: number;
};

export function useVisibleRect(
  ref: RefObject<HTMLElement | null>,
  optionsProp?: Options
) {
  const options = {
    defaultHeight: 800,
    defaultWidth: 400,
    initialY: 0,
    initialX: 0,
    ...optionsProp,
  };

  const [visibleRect, setVisibleRect] = useState<Rect>({
    y: options.initialY,
    height: options.defaultHeight,
    x: options.initialX,
    width: options.defaultWidth,
  });

  /**
   * Cached values are non-reactive and should only be accessed within
   * event handlers or `useEffect`. These values are needed for scroll stabilization
   * and other imperative operations that require synchronous updates
   * to the visible rect.
   */
  const scrollCache = useRef({
    y: options.initialY,
    x: options.initialX,
  });

  useSize(ref, (size) => {
    startTransition(() => {
      setVisibleRect((rect) => {
        if (rect.width === size.width && rect.height === size.height) {
          return rect;
        }

        return {
          ...rect,
          height: size.height,
          width: size.width,
        };
      });
    });
  });

  useEffect(() => {
    function onScroll(e: Event) {
      const top = (e.target as HTMLElement).scrollTop;
      const left = (e.target as HTMLElement).scrollLeft;

      scrollCache.current = {
        y: top,
        x: left,
      };

      startTransition(() => {
        setVisibleRect((rect) => {
          return {
            ...rect,
            y: top,
            x: left,
          };
        });
      });
    }

    const el = ref.current;
    // @ts-expect-error FIXME(strictNullChecks)
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      // @ts-expect-error FIXME(strictNullChecks)
      el.removeEventListener('scroll', onScroll);
    };
  }, [ref]);

  return {
    visibleRect,
    setVisibleRect,
    scrollCache,
  };
}
